import "../Matches/PortalSurfaces.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Hub } from "aws-amplify/utils";
import { ArrowLeft, ChevronDown, Download, LoaderCircle, LockKeyhole, RefreshCw, Search, X } from "lucide-react";
import { getCompetitionAuditPage } from "../../lib/leaderboardApi";
import { buildAuthReturnPath } from "../../lib/authReturn";
import { auditActorLabel, auditChanges, auditContext, auditDate, auditDelivery, auditDeviceLabel, auditDisplayIds, auditFilterOptions, auditHistoryGaps, auditLabel,
  buildCompetitionAuditBlob, createAuditCollector, displayAuditValue, EMPTY_AUDIT_FILTERS, emptyAuditCollection, filterAuditEvents,
  orderAuditEvents, originalTraceUnavailable } from "./competitionAuditData";
import "./Leaderboard.css";
import "./CompetitionMatchAudit.css";

const dateLabel = value => auditDate(value)?.toLocaleString(undefined, {
  month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit", timeZoneName: "short",
}) || "Time not recorded";
const sourceLabels = { participant_outcome: "Participant outcome", bullseye_snapshot: "Score snapshot", bullseye_score: "Bullseye action",
  arc_stage_score: "ARC stage score", arc_match_score: "ARC match summary" };
const gapLabel = source => source.originalTraceStatus === "original_actor_trace_unavailable" ? "Original actor trace unavailable"
  : source.status === "partial_historical_trace" ? "Partial historical trace" : source.status === "snapshot_missing" ? "Score snapshot unavailable"
    : "Historical actions unavailable";

function EventDetail({ item, sources, displayIds }) {
  const [includeUnchanged, setIncludeUnchanged] = useState(false);
  const event = item.auditEvent;
  const context = auditContext(item);
  const fields = useMemo(() => auditChanges(event), [event]);
  const visibleFields = fields.filter(field => includeUnchanged || field.changed);
  const principal = item.identity.startsWith("outcome_decision:") ? "Decision actor" : "Original actor";
  const originalUnavailable = originalTraceUnavailable(item);
  const delivery = auditDelivery(item);
  return <div className="match-audit-event-detail">
    {delivery && <p className={`match-audit-delivery match-audit-delivery--${delivery.tone}`}>{delivery.label}</p>}
    <dl className="match-audit-provenance">
      {originalUnavailable && <div><dt>Original actor / time</dt><dd>Not recorded in retained history</dd></div>}
      <div><dt>{principal}</dt><dd>{auditActorLabel(event, displayIds.actor)}</dd></div>
      <div><dt>{originalUnavailable ? "Decision device" : "Original device"}</dt><dd>{auditDeviceLabel(event, displayIds.device)}</dd></div>
      <div><dt>{originalUnavailable ? "Decision record time" : "Recorded at"}</dt><dd>{dateLabel(event.createdAt)}</dd></div>
      <div><dt>Stage / string</dt><dd>{[context.stageName, context.stringName].filter(Boolean).join(" / ") || "Not recorded"}</dd></div>
    </dl>
    <div className="match-audit-change-heading">
      <h3>Before / After</h3>
      {fields.some(field => !field.changed) && <label className="match-audit-check">
        <input type="checkbox" checked={includeUnchanged} onChange={e => setIncludeUnchanged(e.target.checked)} /> Include unchanged fields
      </label>}
    </div>
    {visibleFields.length ? <div className="match-audit-changes-wrap"><table className="match-audit-changes">
      <thead><tr><th scope="col">Field</th><th scope="col">Before</th><th scope="col">After</th></tr></thead>
      <tbody>{visibleFields.slice(0, 200).map(field => <tr key={field.key} data-changed={field.changed}>
        <th scope="row">{field.label}</th><td>{field.beforeDisplay ?? displayAuditValue(field.before, field.key)}</td><td>{field.afterDisplay ?? displayAuditValue(field.after, field.key)}</td>
      </tr>)}</tbody>
    </table>{visibleFields.length > 200 && <p className="match-audit-muted">200 of {visibleFields.length} fields shown.</p>}</div>
      : <p className="match-audit-muted">{fields.length ? "No changed fields recorded." : "Before and after values were not recorded."}</p>}
    <details className="match-audit-trace">
      <summary>Trace IDs and source references ({item.sourceReferences.length})</summary>
      <dl className="match-audit-provenance match-audit-ids">
        {[["Event identity", item.identity], ["Event ID", event.id], ["Match ID", event.matchId], [originalUnavailable ? "Decision actor ID" : "Original actor ID", event.actorUserId],
          [originalUnavailable ? "Decision device ID" : "Original device ID", event.deviceId], ["Participant ID", context.participantId], ["Entry ID", context.entryId],
          ["Recorded participant ID", event.recordedParticipantId], ["Recorded entry ID", event.recordedEntryId],
          ["Stage ID", context.stageId], ["String ID", context.stringId], [originalUnavailable ? "Decision timestamp" : "Original timestamp", event.createdAt],
          ["Original source", event.source]].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{displayAuditValue(value)}</dd></div>)}
      </dl>
      <ul className="match-audit-references">{item.sourceReferences.map((ref, index) => {
        const source = sources.get(ref.sourceId);
        return <li key={`${ref.sourceId}:${index}`}>
        <strong>Source {index + 1}</strong><code>{ref.sourceId}</code>
        <dl className="match-audit-provenance">
          <div><dt>Uploaded by</dt><dd>{ref.receipt?.uploadedByUserId || "Uploader not recorded"}</dd></div>
          <div><dt>Received by cloud</dt><dd>{ref.receipt?.receivedAt ? dateLabel(ref.receipt.receivedAt) : "Receipt not recorded"}</dd></div>
          <div><dt>Source participant</dt><dd>{ref.context.participantId || "Not recorded"}</dd></div>
          <div><dt>Source entry</dt><dd>{ref.context.entryId || "Not recorded"}</dd></div>
          {source?.deliveryStatus && <div><dt>Delivery</dt><dd>{auditLabel(source.deliveryStatus)}</dd></div>}
          {source?.snapshot?.status && <div><dt>Decision / snapshot state</dt><dd>{auditLabel(source.snapshot.status)}</dd></div>}
          {source?.authoritativeAuditEvent && <>
            <div><dt>Authoritative decision ID</dt><dd>{source.authoritativeAuditEvent.id || "Not recorded"}</dd></div>
            <div><dt>Authoritative decision actor</dt><dd>{source.authoritativeAuditEvent.actorUserId || "Not recorded"}</dd></div>
          </>}
        </dl>
      </li>; })}</ul>
    </details>
  </div>;
}

function MatchAudit({ matchId, discipline }) {
  const empty = useCallback(() => ({ collection: emptyAuditCollection(matchId, discipline), loading: true, error: "", status: null }), [matchId, discipline]);
  const [result, setResult] = useState(empty);
  const [revision, setRevision] = useState(0);
  const [filters, setFilters] = useState({ ...EMPTY_AUDIT_FILTERS });
  const [order, setOrder] = useState("newest");
  const [expanded, setExpanded] = useState(null);
  const [visibleCount, setVisibleCount] = useState(50);
  const [downloadError, setDownloadError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const generation = useRef(0);
  const controller = useRef(null);
  const downloadController = useRef(null);
  const refresh = useCallback(() => {
    generation.current++;
    controller.current?.abort();
    downloadController.current?.abort();
    setDownloading(false);
    setResult(empty()); setDownloadError(""); setExpanded(null); setRevision(value => value + 1);
  }, [empty]);
  useEffect(() => Hub.listen("auth", ({ payload }) => {
    if (["signedOut", "signedIn", "tokenRefresh_failure"].includes(payload.event)) refresh();
  }), [refresh]);

  useEffect(() => {
    const requestGeneration = ++generation.current;
    const abort = new AbortController();
    controller.current = abort;
    const collector = createAuditCollector(matchId, discipline);
    let cursor = null, complete = false, lastPublishedAt = 0;
    const current = () => generation.current === requestGeneration && !abort.signal.aborted;
    setResult({ collection: collector.snapshot(), loading: true, error: "", status: null });
    async function collect() {
      try {
        if (!["arc", "bullseye"].includes(discipline) || !matchId) throw new Error("This audit route does not identify a supported match.");
        do {
          const page = await getCompetitionAuditPage(matchId, discipline, { nextToken: cursor, signal: abort.signal });
          if (!current()) return;
          const progress = collector.append(page);
          cursor = progress.nextToken; complete = progress.complete;
          if (complete || progress.pages === 1 || Date.now() - lastPublishedAt >= 250) {
            setResult({ collection: collector.snapshot(), loading: !complete, error: "", status: null });
            lastPublishedAt = Date.now();
          }
        } while (!complete);
      } catch (error) {
        if (!current()) return;
        const denied = [401, 403].includes(error.status) || /sign in required/i.test(error.message);
        setResult({ collection: denied ? emptyAuditCollection(matchId, discipline) : { ...collector.snapshot(), complete: false },
          loading: false, error: error.message || "Audit history could not be collected.", status: error.status || (denied ? 401 : null), code: error.code });
      }
    }
    collect();
    return () => { abort.abort(); downloadController.current?.abort(); };
  }, [matchId, discipline, revision]);

  const { collection, loading, error, status, code } = result;
  const displayIds = useMemo(() => auditDisplayIds(collection.events), [collection.events]);
  const options = useMemo(() => auditFilterOptions(collection.events, displayIds), [collection.events, displayIds]);
  const filtered = useMemo(() => orderAuditEvents(filterAuditEvents(collection.events, filters), order), [collection.events, filters, order]);
  const gaps = useMemo(() => auditHistoryGaps(collection.sources), [collection.sources]);
  const sourcesById = useMemo(() => new Map(collection.sources.map(source => [source.sourceId, source])), [collection.sources]);
  const activeFilters = Object.values(filters).some(Boolean);
  const accessDenied = [401, 403].includes(status);
  const capacityExceeded = code === "AUDIT_CAPACITY_LIMIT";
  const canDownload = collection.complete && !loading && !error && !downloading;
  const changeFilter = (key, value) => { setFilters(previous => ({ ...previous, [key]: value })); setVisibleCount(50); setExpanded(null); };
  async function download(scope) {
    if (!canDownload) return;
    const abort = new AbortController();
    downloadController.current?.abort(); downloadController.current = abort;
    const requestGeneration = generation.current;
    setDownloading(true); setDownloadError("");
    try {
      const file = await buildCompetitionAuditBlob(collection, scope, filters, { signal: abort.signal });
      if (abort.signal.aborted || generation.current !== requestGeneration) return;
      const url = URL.createObjectURL(file);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${discipline}-${matchId.replace(/[^a-zA-Z0-9_-]/g, "-")}-audit-${scope}.json`;
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDownloadError("");
    } catch (error) {
      if (!abort.signal.aborted && generation.current === requestGeneration) setDownloadError("The audit file could not be prepared. The collected history is unchanged.");
    } finally {
      if (downloadController.current === abort && generation.current === requestGeneration) setDownloading(false);
    }
  }
  const workbench = `/matches/manage/${discipline}/${encodeURIComponent(matchId)}`;
  return <section className="leaderboard-page portal-surface match-audit-page">
    <Helmet><title>Scoring history | Range Pocket Competition</title><meta name="robots" content="noindex,nofollow" /></Helmet>
    <div className="leaderboard-shell">
      <Link to={workbench} className="match-audit-back"><ArrowLeft size={16} aria-hidden="true" /> Match workbench</Link>
      <header className="match-audit-header">
        <div><span className="match-audit-access"><LockKeyhole size={13} aria-hidden="true" /> Owner only</span>
          <h1>Scoring history</h1><p>{collection.matchName || (discipline === "bullseye" ? "Bullseye match" : "ARC match")}</p></div>
        <div className="match-audit-tools">
          <button type="button" className="match-audit-icon" aria-label="Refresh audit history" title="Refresh audit history" onClick={refresh}>
            <RefreshCw size={18} aria-hidden="true" /></button>
          <button type="button" onClick={() => download("full")} disabled={!canDownload}><Download size={16} aria-hidden="true" /> Full JSON</button>
          <button type="button" onClick={() => download("filtered")} disabled={!canDownload || !filtered.length}><Download size={16} aria-hidden="true" /> Filtered JSON</button>
        </div>
      </header>
      <p className="match-audit-scope">Review score changes, confirmations and participant outcome decisions uploaded from your devices. Registration edits, schedule changes and announcements appear in their respective workspaces.</p>
      {!accessDenied && <div className="match-audit-collection" role="status" aria-live="polite">
        <strong>{loading ? <><LoaderCircle size={16} className="match-audit-spinner" aria-hidden="true" /> Collecting history</>
          : error ? "Collection incomplete" : "Available scoring history loaded"}</strong>
        <span>{collection.events.length} recorded scoring events</span>
      </div>}
      {error && <div className="match-audit-message match-audit-error" role="alert">
        <strong>{capacityExceeded ? "Browser export limit reached" : status === 403 ? "Owner access required" : status === 401 ? "Sign in required" : status === 409 ? "History changed or conflicts" : "History unavailable"}</strong>
        {accessDenied ? <p>{status === 401 ? "Sign in with the match owner's account." : "This account does not own this match."}</p>
          : <><p>{error}</p><p>Downloads are unavailable until collection completes successfully.</p></>}
        {!capacityExceeded && <div className="match-audit-tools"><button type="button" onClick={refresh}><RefreshCw size={16} aria-hidden="true" /> {accessDenied ? "Retry" : "Restart collection"}</button>
          {accessDenied && <Link to={buildAuthReturnPath("/auth/sign-in", `${workbench}/audit`)}>Sign in</Link>}</div>
        }
      </div>}
      {downloading && <div className="match-audit-tools" role="status"><span>Preparing JSON download</span>
        <button type="button" onClick={() => downloadController.current?.abort()}><X size={16} aria-hidden="true" /> Cancel download</button></div>}
      {downloadError && <p className="match-audit-message match-audit-error" role="alert">{downloadError}</p>}
      {!accessDenied && <>
      <div className="match-audit-filters" aria-label="Audit filters">
        <label className="match-audit-search">Search history<span><Search size={17} aria-hidden="true" />
          <input type="search" value={filters.search} placeholder="Name, score, action or ID" onChange={e => changeFilter("search", e.target.value)} /></span></label>
        {[["participant", "Participant"], ["entry", "Entry"], ["device", "Device"], ["action", "Action"]].map(([key, label]) =>
          <label key={key}>{label}<select value={filters[key]} onChange={e => changeFilter(key, e.target.value)}>
            <option value="">All {key === "entry" ? "entries" : `${key}s`}</option>
            {options[key].map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select></label>)}
      </div>
      <div className="match-audit-list-heading">
        <h2>Events <span>{filtered.length} of {collection.events.length}</span></h2>
        <div className="match-audit-tools">{activeFilters && <button type="button" className="match-audit-clear" onClick={() => {
          setFilters({ ...EMPTY_AUDIT_FILTERS }); setVisibleCount(50); setExpanded(null);
        }}><X size={16} aria-hidden="true" /> Clear filters</button>}
          <label className="match-audit-order"><span className="match-audit-sr-only">Event order</span>
            <select value={order} onChange={e => setOrder(e.target.value)}><option value="newest">Newest first</option><option value="oldest">Oldest first</option></select></label>
        </div>
      </div>
      {filtered.length ? <div className="match-audit-events">
        {filtered.slice(0, visibleCount).map(item => {
          const event = item.auditEvent, context = auditContext(item);
          const delivery = auditDelivery(item), originalUnavailable = originalTraceUnavailable(item);
          return <details className="match-audit-event" key={item.identity} open={expanded === item.identity}>
            <summary onClick={e => { e.preventDefault(); setExpanded(previous => previous === item.identity ? null : item.identity); }}>
              <time dateTime={auditDate(event.createdAt)?.toISOString()}>{dateLabel(event.createdAt)}{originalUnavailable && <span>Decision record time</span>}</time>
              <div className="match-audit-event-main"><strong>{event.title || auditLabel(event.action)}</strong>
                {delivery && <span className={`match-audit-delivery match-audit-delivery--${delivery.tone}`}>{delivery.label}</span>}
                <span>{[context.competitorName || "Participant not recorded", context.entryLabel, context.stageName, context.stringName].filter(Boolean).join(" / ")}</span>
                {event.detail && <p>{String(event.detail)}</p>}</div>
              <div className="match-audit-event-origin"><span>{originalUnavailable ? "Original device not recorded" : auditDeviceLabel(event, displayIds.device)}</span>
                <span>{originalUnavailable ? "Unknown historical actor" : auditActorLabel(event, displayIds.actor)}</span></div>
              <ChevronDown size={17} className="match-audit-chevron" aria-hidden="true" />
            </summary>
            {expanded === item.identity && <EventDetail item={item} sources={sourcesById} displayIds={displayIds} />}
          </details>;
        })}
        {filtered.length > visibleCount && <button type="button" className="match-audit-more" onClick={() => setVisibleCount(count => count + 50)}>Show more events ({filtered.length - visibleCount} remaining)</button>}
      </div> : <div className="match-audit-empty">{loading ? "Waiting for matching history..." : error ? "No matching events collected."
        : collection.events.length ? "No events match these filters." : "No scoring actions have been retained for this match. Scores may still be available in the results."}</div>}
      {gaps.length > 0 && <details className="match-audit-gaps">
        <summary>Earlier history is incomplete for {gaps.length} {gaps.length === 1 ? "record" : "records"}</summary>
        <p>Some devices uploaded scores without earlier actions or actor details. This does not mean the score is missing. Expand a record’s references when investigating a discrepancy.</p>
        <ul>{gaps.map(source => <li key={source.sourceId}>
          <strong>{[source.context.competitorName, source.context.entryLabel, source.context.stageName].filter(Boolean).join(" / ") || sourceLabels[source.kind] || "Historical source"}</strong>
          <span>{gapLabel(source)}</span>
          {source.snapshot && <span>{source.kind === "participant_outcome" ? auditLabel(source.snapshot.outcomeStatus)
            : source.snapshot.points !== undefined ? `${source.snapshot.points} points / ${source.snapshot.xCount ?? 0} X` : auditLabel(source.status)}</span>}
          <code>{source.sourceId}</code>
        </li>)}</ul>
      </details>}
      {collection.pages > 0 && <details className="match-audit-gaps"><summary>Collection diagnostics</summary>      {collection.collectionStartedAt && <div className="match-audit-read-window">
        <span>Read window: {dateLabel(collection.collectionStartedAt)} to {dateLabel(collection.collectionFinishedAt)}</span>
        <span>Non-transactional collection</span>
      </div>}
<footer className="match-audit-footer">
        <span>{collection.sources.length} sources · {collection.pages} pages</span>
        <span>{collection.eventOccurrences} recorded references / {collection.events.length} unique events</span>
        <span>{gaps.length} records with incomplete earlier history</span>
        {collection.sources.some(source => source.status === "recorded_from_first_upload") &&
          <span>{collection.sources.filter(source => source.status === "recorded_from_first_upload").length} snapshots recorded since first cloud upload</span>}
      </footer></details>}
      </>}
    </div>
  </section>;
}

export default function CompetitionMatchAudit({ discipline }) {
  const { matchId } = useParams();
  return <MatchAudit key={`${discipline}:${matchId}`} matchId={matchId} discipline={discipline} />;
}

export const ArcMatchAudit_Page = { path: "/matches/manage/arc/:matchId/audit", element: <CompetitionMatchAudit discipline="arc" /> };
export const BullseyeMatchAudit_Page = { path: "/matches/manage/bullseye/:matchId/audit", element: <CompetitionMatchAudit discipline="bullseye" /> };
